import { supabase } from './supabase'

/**
 * Which household this device syncs into, and which member is holding it.
 *
 * Rows are scoped to a household, not to a user (`docs/architecture-review.md`
 * §1.1): a *member* is a person in the house and the unit chores are assigned
 * to, a *user* is a login. This module is where the app learns both ids.
 *
 * They are cached in `localStorage` rather than only in memory because both are
 * needed before the network is: the member id is a component of every
 * completion id, so a device that starts offline must still compose ids that
 * the other device will recognise. Versioned like every other key here so it
 * can be abandoned rather than migrated.
 */
export interface Membership {
  householdId: string
  memberId: string
  /**
   * Whether this account created the household.
   *
   * Presentation only. The owner is `households.owner_user_id` and the database
   * is what enforces it — every write to `household_members` goes through a
   * policy that checks it, so a client that lied here would render the buttons
   * and then be refused. It is cached with the rest of the membership so the
   * *Foyer* screen can decide what to draw before the network answers, and
   * while it never does.
   */
  isOwner: boolean
}

/**
 * `_v2`: the cached shape gained `isOwner` with members.
 *
 * Renamed rather than migrated, which is what versioning this key was for. Every
 * install resolves its membership once more on the next start, and that is
 * idempotent — `ensure_household` returns the household that exists.
 */
const MEMBERSHIP_KEY = 'maison_zen_household_v2'

/**
 * The member id used when no backend is configured.
 *
 * Local-only mode is how the UI is developed and how the tests run; it never
 * reaches a server, so nothing has to recognise these ids. A real install
 * always has Supabase baked in at build time and resolves a real member id
 * before the app renders.
 */
export const LOCAL_MEMBER_ID = 'local'

let membership: Membership | null = null

function readCache(): Membership | null {
  try {
    const raw = localStorage.getItem(MEMBERSHIP_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Membership>
    if (typeof parsed.householdId !== 'string' || typeof parsed.memberId !== 'string') return null
    if (!parsed.householdId || !parsed.memberId) return null
    return {
      householdId: parsed.householdId,
      memberId: parsed.memberId,
      isOwner: parsed.isOwner === true,
    }
  } catch {
    return null
  }
}

function writeCache(value: Membership): void {
  try {
    localStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(value))
  } catch {
    // Private mode or a full quota. The next start resolves it again, which is
    // idempotent — `ensure_household` returns the membership that exists.
  }
}

/** The membership this device already knows about, without touching the network. */
export function currentMembership(): Membership | null {
  if (!supabase) return null
  membership ??= readCache()
  return membership
}

/**
 * The member component of every completion id written on this device.
 *
 * Never empty: an empty component would make two children who share a chore
 * share a row, and `encodeCompletionId` refuses it outright. Callers must have
 * resolved a membership first, which is what `AuthGate` guarantees before it
 * renders the app.
 */
export function currentMemberId(): string {
  if (!supabase) return LOCAL_MEMBER_ID
  const known = currentMembership()
  if (!known) throw new Error('No household resolved yet')
  return known.memberId
}

/**
 * Resolve the caller's household, creating one on first sign-in.
 *
 * The work happens in `public.ensure_household`, a `security definer` function
 * added by `0003`. It has to be privileged: the RLS policies deny a signed-in
 * user who is not yet in a household — including the household that would give
 * them one — and loosening them would let anyone holding the anon key insert
 * themselves into a household whose id they guess. So the bootstrap is one
 * function that reads `auth.uid()` itself and never accepts a user id.
 *
 * Since `0006` it does one more thing before it would create anything: it claims
 * an invitation waiting on the caller's address. That order is the whole reason
 * the claim lives inside the function rather than beside this call — an invited
 * member who reached the create branch first would get an empty household of
 * their own, and the invitation could never be redeemed afterwards.
 */
export async function resolveMembership(displayName = ''): Promise<Membership> {
  const client = supabase
  if (!client) throw new Error('Supabase is not configured')

  const { data, error } = await client.rpc('ensure_household', { display_name: displayName })
  if (error) throw error

  // A set-returning function comes back as an array through PostgREST.
  const row = (Array.isArray(data) ? data[0] : data) as
    | { household_id?: unknown; member_id?: unknown; is_owner?: unknown }
    | undefined

  const householdId = typeof row?.household_id === 'string' ? row.household_id : ''
  const memberId = typeof row?.member_id === 'string' ? row.member_id : ''

  if (!householdId || !memberId) {
    throw new Error('ensure_household returned no household')
  }

  membership = { householdId, memberId, isOwner: row?.is_owner === true }
  writeCache(membership)
  return membership
}

/**
 * Forget the household on sign-out.
 *
 * Without this the next account to sign in on this browser would inherit the
 * previous one's scope and push its ticks into the wrong household. Only the
 * binding is dropped — never the local data, which is the rule `AuthGate`
 * exists to protect.
 */
export function forgetMembership(): void {
  membership = null
  try {
    localStorage.removeItem(MEMBERSHIP_KEY)
  } catch {
    // Nothing to forget if storage is unavailable.
  }
}
